import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import { Record as ReactAdminRecord, useDataProvider } from 'react-admin';
import OptionsHistoryModal from './OptionsHistoryModal';
import type { OptionsHistoryRow } from './OptionsHistoryModal';

const TRIM_COLUMNS: OptionsHistoryRow = [
  'Contractor Name',
  'Approved Date',
  'Approved By',
];

interface TrimApproval extends ReactAdminRecord {
  check?: boolean;
  typeOption?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
}

// Subset of a job-lot-phase-report row the status needs.
export interface TrimOptionPhase {
  id: string;
  approved?: boolean | null;
  trimOptionSelected?: boolean | null;
  dateApproved?: string | Date | null;
  approvingUserFullName?: string | null;
  contractorName?: string | null;
}

interface TrimOptionStatusProps {
  jobId?: string;
  hasTrimOptions?: boolean;
  trimOptionsFinished?: boolean | null;
  trimOptionsFinishedJobPhaseId?: string | null;
  jobPhases?: TrimOptionPhase[];
  fullWidth?: boolean;
}

// job-lot-report serves numeric columns as strings ("150.00").
function formatPayoutAmount(
  amount: number | string | null | undefined,
): string {
  if (amount === null || amount === undefined || amount === '') return '-';
  const value = Number(amount);
  return Number.isFinite(value) ? `$${value.toFixed(2)}` : '-';
}

// The finalizing phase row is authoritative. The bump auto-approve path does
// not stamp approvingUserId on the phase, so the Trim history row (written by
// the backend on finalize) supplies the approver for those phases.
function buildApprovalRow(
  phase: TrimOptionPhase | undefined,
  approval: TrimApproval | undefined,
): OptionsHistoryRow {
  const historyName = [approval?.user?.firstName, approval?.user?.lastName]
    .filter(Boolean)
    .join(' ');

  return [
    phase?.contractorName || 'N/A',
    phase?.dateApproved
      ? new Date(phase.dateApproved).toLocaleDateString()
      : 'N/A',
    phase?.approvingUserFullName || historyName || 'N/A',
  ];
}

export function TrimOptionStatus({
  jobId,
  hasTrimOptions,
  trimOptionsFinished,
  trimOptionsFinishedJobPhaseId,
  jobPhases,
  fullWidth = false,
}: TrimOptionStatusProps): React.ReactElement | null {
  const dataProvider = useDataProvider();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approval, setApproval] = useState<TrimApproval>();
  const [payoutAmount, setPayoutAmount] = useState<string>();

  if (!hasTrimOptions) {
    return null;
  }

  const finalizingPhase = trimOptionsFinishedJobPhaseId
    ? jobPhases?.find(phase => phase.id === trimOptionsFinishedJobPhaseId)
    : undefined;
  // Selected on a phase at request/log time; finalized by the backend on approval.
  const isSelected =
    trimOptionsFinished === true ||
    Boolean(jobPhases?.some(phase => phase.trimOptionSelected === true));
  const isApproved =
    trimOptionsFinished === true && finalizingPhase?.approved === true;

  // Payout is a snapshot taken when the modal opens, not refreshed after.
  const openDetails = async () => {
    if (!jobId) return;

    setApproval(undefined);
    setPayoutAmount(undefined);
    setLoading(true);
    setOpen(true);

    try {
      const [jobResult, historyResult] = await Promise.all([
        dataProvider.getOne('job-lot-report', { id: jobId }),
        dataProvider.getList<TrimApproval>(
          'job-interior-exterior-options-history-report',
          {
            filter: { jobId, typeOption: 'Trim', check: true },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'date', order: 'DESC' },
          },
        ),
      ]);
      setApproval(
        (historyResult.data || []).find(
          item => item.typeOption === 'Trim' && item.check === true,
        ),
      );
      // The report yields NULL when the cost or the governing % is undefined.
      setPayoutAmount(formatPayoutAmount(jobResult.data?.trimCalculatedCost));
    } catch {
      setApproval(undefined);
      setPayoutAmount(formatPayoutAmount(undefined));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      width={fullWidth ? '100%' : undefined}
      display="flex"
      alignItems="center"
      mr={2}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={isSelected}
            name="trimOptionsFinished"
            disabled
            color="primary"
          />
        }
        label="Trim Option"
      />
      {isApproved && (
        <IconButton
          aria-label="Trim Option details"
          onClick={openDetails}
          size="small"
          style={{ color: '#1976d2' }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      )}

      <OptionsHistoryModal
        open={open}
        onClose={() => setOpen(false)}
        title="Trim Option"
        columns={TRIM_COLUMNS}
        rows={[buildApprovalRow(finalizingPhase, approval)]}
        loading={loading}
        payoutAmount={loading ? undefined : payoutAmount}
      />
    </Box>
  );
}
